'use client';

import { useScreenDetect } from '@sgcmpteltd/uikit-next';
import {
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  setNewcarItemsCookies,
} from '@/actions/new-car/set-newcar-items-cookies';
import type { CompareItem } from '@/app/(routes)/listing/types';
import { ComparisonDrawer } from '@/components/comparison-drawer';
import {
  NUM_COMPARISON_MAX_VEHICLES,
} from '@/components/comparison-drawer/contants';
import { ReportErrorModal } from '@/components/modals/report-error-modal';
import {
  TITLE_HEADING_H2,
  TITLE_HEADING_H3,
} from '@/components/section-container/base';
import {
  TITLE_HEADING_DIV,
} from '@/components/section-container/base/constants';
import { SectionContainer } from '@/components/section-container/page';
import { TextLink } from '@/components/text-link';
import { InfoContext } from '@/context/info';
import { useLocalStorage } from '@/hooks/use-localStorage';
import { joinSgcmMsSite, joinSgcmSite } from '@/utils/url/join/public';

import { Alert } from '../../../_components/page-content/alert';
import {
  PAST_CAR_TERMINATED_PROMPT,
} from '../../../_components/page-content/alert/constants';
import {
  BrochureModal,
} from '../../../_components/page-content/brochure-modal';
import {
  NEW_CARS_COMPARE_LIST_LOCAL_STORAGE_KEY,
  SECTION_TITLE_RECOMMENDED_FOR_YOU,
} from '../../../_components/page-content/constants';
import {
  RecommendedForYouForInfo,
} from '../../../_components/page-content/recommended-for-you-for-info';
import {
  FeaturesInfoTable,
} from '../../../_components/page-content/submodel-list/mobile/submodel-info-table/features-info'; // @suppress longLineCheck
import {
  SubmodelToggleCheckbox,
} from '../../../_components/page-content/submodel-toggle-checkbox';
import {
  getMatchStatusSubModels,
} from '../../../_components/page-content/utils';
import {
  BUTTON_TEXT_DOWNLOAD_BROCHURE,
  CHECKBOX_SECTION_DESC,
  ERROR_MESSAGE,
  SECTION_TITLE_FEATURES,
} from './constant';
import { Item } from './related-item';
import styles from './styles.module.scss';
import type { Component } from './types';

export const PageContent: Component = ({
  compareList = null,
  relatedItems,
}) => {
  const { isDesktop } = useScreenDetect();
  const isDesktopMode = isDesktop();
  const { carDetail } = useContext(InfoContext);
  const { selectedSubmodelId, setSelectedSubmodelId } = useContext(InfoContext);
  const {
    id, make, model, subModels, brochureFiles, slug,
  } = carDetail;
  const fullName = `${make} ${model}`;
  const hasBrochureFiles = brochureFiles.length !== 0;
  const hasRelatedItem = relatedItems.length !== 0;

  const [
    isBrochureModalOpen,
    setIsBrochureModalOpen,
  ] = useState(false);
  const [
    compareItems,
    setCompareItems,
  ] = useLocalStorage<CompareItem[]>(
    NEW_CARS_COMPARE_LIST_LOCAL_STORAGE_KEY,
    compareList ?? [],
  );
  const [
    isComparisonDrawerOpen,
    setIsComparisonDrawerOpen,
  ] = useState(false);
  const [
    isComparisonWarningModalOpen,
    setIsComparisonWarningModalOpen,
  ] = useState(false);

  const matchingSubModel = getMatchStatusSubModels(
    carDetail.additionalProperties.isAvailable,
    subModels,
  );

  const [
    selectedSubmodelSpecs,
    setSelectedSubmodelSpecs,
  ] = useState(
    subModels.find(
      submodel => submodel.id === selectedSubmodelId,
    ) || matchingSubModel[0],
  );

  const [
    isReportErrorModalOpen,
    setIsReportErrorModalOpen,
  ] = useState(false);

  const isComparedSubmodelArray = useMemo(() => {
    const comparedSubmodelArray = matchingSubModel.map(
      submodel => compareItems.some(
        compareItem => compareItem.submodelId === submodel.id,
      ),
    );

    return comparedSubmodelArray;
  }, [compareItems, matchingSubModel]);

  const handleCompareClearAllClick = async () => {
    const formData = new FormData();

    formData.set('submodelIds', '');
    formData.set('modelIds', '');

    setIsComparisonDrawerOpen(false);
    setCompareItems([]);
    setIsComparisonDrawerOpen(false);
    await setNewcarItemsCookies(formData);
  };

  const handleCompareItemRemove = async (submodelId: number) => {
    const formData = new FormData();
    const newCompareItems = compareItems.filter(
      item => item.submodelId !== submodelId,
    );

    formData.set(
      'submodelIds',
      newCompareItems
        .map(item => item.submodelId)
        .join(','),
    );

    formData.set(
      'modelIds',
      newCompareItems
        .map(item => item.id)
        .join(','),
    );

    if (newCompareItems.length === 0) {
      setIsComparisonDrawerOpen(false);
    }
    setCompareItems(newCompareItems);

    await setNewcarItemsCookies(formData);
  };

  const handleDrawerToggleClick = () => {
    setIsComparisonDrawerOpen(prev => !prev);
  };

  const handleUpdateCompareList = async (
    compareItem: CompareItem,
    isCompared: boolean,
  ) => {
    const formData = new FormData();

    let newCompareItems: CompareItem[];

    if (isCompared) {
      newCompareItems = [...compareItems, compareItem];
      setIsComparisonDrawerOpen(true);
    } else {
      newCompareItems = compareItems.filter(
        item => item.submodelId !== compareItem.submodelId,
      );

      if (newCompareItems.length === 0) {
        setIsComparisonDrawerOpen(false);
      }
    }

    if (newCompareItems.length > NUM_COMPARISON_MAX_VEHICLES) {
      setIsComparisonWarningModalOpen(true);
      return;
    }

    formData.set(
      'submodelIds',
      newCompareItems
        .map(item => item.submodelId)
        .join(','),
    );

    formData.set(
      'modelIds',
      newCompareItems
        .map(item => item.id)
        .join(','),
    );

    setCompareItems(newCompareItems);

    await setNewcarItemsCookies(formData);
  };

  const handleCheckboxChange = (submodelId: number, index: number) => {
    handleUpdateCompareList({
      id: carDetail.id,
      submodelId,
      make,
      model,
      name: matchingSubModel[index].name,
      thumbnailUrl: `https://i.i-sgcm.com/new_cars/cars/${carDetail.id}/${carDetail.id}_m.jpg`,
    }, !isComparedSubmodelArray[index]);
  };

  const handleSelectSubmodel = (submodelId: number) => {
    const url = new URL(window.location.href);

    url.searchParams.set('submodel_id', submodelId.toString());
    window.history.pushState({}, '', url);
  };

  const handleBrochureClick = () => {
    setIsBrochureModalOpen(true);
  };

  const handleReportErrorClick = () => {
    setIsReportErrorModalOpen(true);
  };

  useEffect(() => {
    if (!selectedSubmodelId) {
      setSelectedSubmodelId(matchingSubModel[0].id);
    }
  }, [selectedSubmodelId, setSelectedSubmodelId, matchingSubModel]);

  useEffect(() => {
    const newSelectedSubmodel = subModels.find(
      submodel => submodel.id === selectedSubmodelId,
    ) || matchingSubModel[0];

    setSelectedSubmodelSpecs(newSelectedSubmodel);
  }, [selectedSubmodelId, subModels, matchingSubModel]);

  const isPastCar = !(carDetail.additionalProperties.isAvailable
    || carDetail.additionalProperties.isUpcoming);

  return (
    <main
      className={styles.containerPageRoot}>
      <ReportErrorModal
        isOpen={isReportErrorModalOpen}
        onClose={() => {
          setIsReportErrorModalOpen(false);
        }} />
      {hasBrochureFiles
      && (
        <BrochureModal
          brochureList={brochureFiles}
          isOpen={isBrochureModalOpen}
          onClose={() => {
            setIsBrochureModalOpen(false);
          }} />
      )}
      {
        isPastCar
        && (
          <SectionContainer
            title=""
            titleHeading={TITLE_HEADING_DIV}>
            <Alert
              date={carDetail.terminationDate}
              text={PAST_CAR_TERMINATED_PROMPT} />
          </SectionContainer>
        )
      }
      <SectionContainer
        className={styles.containerPageContent}
        title={SECTION_TITLE_FEATURES}
        titleHeading={TITLE_HEADING_H3}>
        <div className={styles.containerContent}>
          <div className={styles.containerAsideColumn}>
            <div className={styles.containerSubmodelToggleCheckboxs}>
              <div className={styles.textCheckboxDesc}>
                {CHECKBOX_SECTION_DESC}
              </div>
              {matchingSubModel.map((submodel, index) => {
                const submodelName = `${submodel.name} (${submodel.transmissionType})`;

                return (
                  <SubmodelToggleCheckbox
                    key={submodel.id}
                    isFacelift={submodel.isFacelift}
                    isNewVariant={submodel.isNewVariant}
                    isOnCompared={isComparedSubmodelArray[index]}
                    isSelected={selectedSubmodelId === submodel.id}
                    submodelId={submodel.id}
                    submodelIndex={index}
                    submodelName={submodelName}
                    onComparedCheckboxChange={handleCheckboxChange}
                    onSelectSubmodel={handleSelectSubmodel} />
                );
              })}
            </div>
            {hasRelatedItem && (
              <div className={styles.containerRelatedItems}>
                <div className={styles.containerHeading}>
                  <div
                    className={styles.containerTitle}>
                    <div
                      className={styles.markTitle} />
                    <span
                      className={styles.textTitle}>
                      Related Items
                    </span>
                  </div>
                  <TextLink
                    className={styles.linkViewAll}
                    href={`https://www.mycarforum.com/index.php?app=marketplace&controller=listing&car_make=${carDetail.make}&car_model=${carDetail.model}&what=available`}
                    text="View All" />
                </div>
                {relatedItems.map(item => (
                  <Item
                    key={`relatedItem${item.id}`}
                    carId={id}
                    relatedItem={item} />
                ))}
              </div>
            )}
          </div>
          <div className={styles.containerFeatures}>
            <div className={styles.containerFeatureHeader}>
              <div className={styles.textModelFullName}>
                {fullName}
                {' '}
                {selectedSubmodelSpecs.name}
                {' '}
                (
                {selectedSubmodelSpecs.transmissionType}
                )
              </div>
              {hasBrochureFiles && (
                <button
                  className={styles.btnDownloadBrochure}
                  onClick={handleBrochureClick}>
                  {BUTTON_TEXT_DOWNLOAD_BROCHURE}
                </button>
              )}
            </div>
            <div className={styles.containerSpecContent}>
              <FeaturesInfoTable
                isAvailable={carDetail.additionalProperties.isAvailable}
                submodel={selectedSubmodelSpecs}
                vehicleType={carDetail.vehicleType} />
            </div>
            <div className={styles.containerErrorDesc}>
              <button
                className={styles.btnReportError}
                onClick={handleReportErrorClick}>
                Spotted an error? Let us know here
              </button>
              <div className={styles.textReportErrorMsg}>
                {ERROR_MESSAGE}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.containerComparisonDrawer}>
          <ComparisonDrawer
            comparePageLinkHref={
              isDesktopMode
                ? `${joinSgcmSite('/new_cars/newcars_compare.php')}`
                : `${joinSgcmMsSite(`new-cars/info/${id}/${slug}/compare`)}`
            }
            isOpen={isComparisonDrawerOpen}
            isWarningModalOpen={isComparisonWarningModalOpen}
            items={compareItems}
            onClearAllBtnClick={handleCompareClearAllClick}
            onCloseClick={handleCompareItemRemove}
            onDrawerToggleClick={handleDrawerToggleClick}
            onWarningModalCloseClick={
              () => setIsComparisonWarningModalOpen(false)
            } />
        </div>
      </SectionContainer>
      <SectionContainer
        className={styles.containerPageContent}
        title={SECTION_TITLE_RECOMMENDED_FOR_YOU}
        titleHeading={TITLE_HEADING_H2}>
        <RecommendedForYouForInfo />
      </SectionContainer>
    </main>
  );
};
